import {
  QUOTATION_EDIT_REQUEST_STATUS_LABEL,
  quotationStatusLabel,
  type QuotationEditRequestStatus,
} from '../../quotationWorkflow';

export const QUOTATION_CHANGE_LOG_FIELD_LABELS: Record<string, string> = {
  quotation_number: 'Nomor quotation',
  preorder: 'Preorder',
  is_contract: 'Kontrak',
  is_consultant_quote: 'Penawaran konsultan',
  contract_period_start_date: 'Tanggal mulai kontrak',
  contract_period_end_date: 'Tanggal kontrak selesai',
  sample_pickup_method: 'Metode ambil sampel',
  discount_amount: 'Diskon (rupiah)',
  is_ppn: 'PPN',
  status: 'Status',
  progress_status: 'Status',
  edit_request_status: 'Status permintaan revisi',
  quotation_date: 'Tanggal quotation',
  delivery_date: 'Tanggal pengiriman',
  receipt_date: 'Tanggal penerimaan',
  payment_date: 'Tanggal pembayaran',
  term_of_payment: 'Term of payment',
  term_of_payment_date: 'Term of payment date',
  term_of_payment_note: 'Term of payment note',
  remark_edit: 'Catatan revisi',
  remark_edit_review: 'Catatan review revisi (SPV)',
  remark_approval: 'Catatan persetujuan',
  customer_documentId: 'Pelanggan (ID)',
  assignee_user_documentId: 'Penanggung jawab (ID)',
  approved_by_user_documentId: 'Disetujui oleh (ID)',
};

export type QuotationChangeLogEntry = {
  field: string;
  label: string;
  old: string;
  new: string;
};

function formatDateValue(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  return s;
}

function formatDateTimeValue(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  const d = new Date(String(v));
  if (Number.isNaN(d.getTime())) return formatPlainValue(v);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatBooleanYesNo(v: unknown): string {
  if (v === true || v === 1 || v === '1' || v === 'true') return 'Ya';
  if (v === false || v === 0 || v === '0' || v === 'false') return 'Tidak';
  return formatPlainValue(v);
}

function formatPlainValue(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Ya' : 'Tidak';
  if (typeof v === 'number') return String(v);
  if (typeof v === 'string') return v.trim() || '—';
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

/** Format satu nilai field sesuai tipe/konteks quotation. */
export function formatQuotationChangeLogValue(field: string, value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';

  switch (field) {
    case 'status':
    case 'progress_status':
      return quotationStatusLabel(typeof value === 'string' ? value : String(value));
    case 'edit_request_status': {
      const s = String(value) as QuotationEditRequestStatus;
      return QUOTATION_EDIT_REQUEST_STATUS_LABEL[s] ?? String(value);
    }
    case 'preorder':
    case 'is_consultant_quote':
    case 'is_ppn':
    case 'term_of_payment':
      return formatBooleanYesNo(value);
    case 'is_contract':
      if (value === true || value === 1 || value === '1') return 'Kontrak';
      if (value === false || value === 0 || value === '0') return 'Non kontrak';
      return formatPlainValue(value);
    case 'sample_pickup_method':
      if (value === 'diantar') return 'Diantar';
      if (value === 'sampling') return 'Sampling';
      return formatPlainValue(value);
    case 'quotation_date':
    case 'delivery_date':
    case 'receipt_date':
    case 'payment_date':
    case 'term_of_payment_date':
    case 'contract_period_start_date':
    case 'contract_period_end_date':
      return formatDateTimeValue(value);
    case 'discount_amount':
      return formatPlainValue(value);
    default:
      return formatPlainValue(value);
  }
}

export function quotationChangeLogFieldLabel(field: string): string {
  return QUOTATION_CHANGE_LOG_FIELD_LABELS[field] ?? field.replace(/_/g, ' ');
}

function isFieldChange(v: unknown): v is { old: unknown; new: unknown } {
  return (
    v != null &&
    typeof v === 'object' &&
    !Array.isArray(v) &&
    'old' in v &&
    'new' in v
  );
}

/** Parse `change_logs` JSON menjadi daftar perubahan (tanpa `_meta`). */
export function parseQuotationChangeLogs(change_logs: unknown): QuotationChangeLogEntry[] {
  if (change_logs == null || typeof change_logs !== 'object' || Array.isArray(change_logs)) {
    return [];
  }
  const entries: QuotationChangeLogEntry[] = [];
  for (const [field, raw] of Object.entries(change_logs as Record<string, unknown>)) {
    if (field === '_meta' || !isFieldChange(raw)) continue;
    entries.push({
      field,
      label: quotationChangeLogFieldLabel(field),
      old: formatQuotationChangeLogValue(field, raw.old),
      new: formatQuotationChangeLogValue(field, raw.new),
    });
  }
  return entries;
}

export function formatQuotationChangeLogLine(entry: QuotationChangeLogEntry): string {
  return `${entry.label}: ${entry.old} → ${entry.new}`;
}

export function formatQuotationChangeLogsSummary(change_logs: unknown): string {
  return parseQuotationChangeLogs(change_logs).map(formatQuotationChangeLogLine).join('\n');
}

function readChangeLogMeta(change_logs: unknown): Record<string, unknown> | null {
  if (change_logs == null || typeof change_logs !== 'object' || Array.isArray(change_logs)) {
    return null;
  }
  const meta = (change_logs as Record<string, unknown>)._meta;
  if (meta == null || typeof meta !== 'object' || Array.isArray(meta)) return null;
  return meta as Record<string, unknown>;
}

export function quotationChangeLogMetaSource(change_logs: unknown): string | null {
  const meta = readChangeLogMeta(change_logs);
  if (!meta) return null;
  const source = meta.source;
  if (source === 'quotation.db.afterUpdate') return 'Dicatat otomatis saat ubah quotation';
  if (typeof source === 'string' && source.trim()) return source.trim();
  return null;
}

export function quotationChangeLogMetaAt(change_logs: unknown): string | null {
  const meta = readChangeLogMeta(change_logs);
  if (!meta) return null;
  const at = meta.at;
  if (typeof at === 'string' && at.trim()) return at.trim();
  return null;
}
